import { useEffect, useMemo } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import LogoMark from '../assets/Logomark.png'
import { CHECK_MAGIC_LINK_MUTATION, GET_USERDATA_QUERY } from '../services/GraphQLQueries'
import { setAuthToken, storeUserSession } from '../services/auth'

const InvitePage = () => {
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(window.location.search)
  const inviteEmail = searchParams.get('email')
  const inviteUrl = window.location.href
  const hasInviteDetails = Boolean(inviteEmail && inviteUrl)

  const [checkMagicLink, { data, loading, error }] = useMutation(CHECK_MAGIC_LINK_MUTATION)
  const [loadUserData, { data: userData, loading: loadingUserData, error: userDataError, called }] =
    useLazyQuery(GET_USERDATA_QUERY, { fetchPolicy: 'network-only' })

  // 1) Verify invite (leave magicLink = full URL as-is)
  useEffect(() => {
    if (!hasInviteDetails) return

    checkMagicLink({
      variables: {
        email: inviteEmail,
        magicLink: inviteUrl, // full URL (includes #token fragment) - leave alone
      },
    })
  }, [hasInviteDetails, inviteEmail, inviteUrl, checkMagicLink])

  const inviteResponse = data?.checkMagicLink

  // 2) On success: store bearer token ONCE, then fetch user data ONCE
  useEffect(() => {
    if (!inviteResponse) return
    if (inviteResponse.error) return
    if (inviteResponse.isExpired) return
    if (!inviteResponse.hasValidMagicLink) return

    if (inviteResponse.bearerToken) {
      setAuthToken(inviteResponse.bearerToken)
    }

    // Avoid re-firing if mutation rerenders with same data
    if (!called) {
      loadUserData()
    }
  }, [inviteResponse, loadUserData, called])

  // 3) When user data arrives: write session/local storage ONCE (and safely)
  useEffect(() => {
    if (!userData) {
      return
    }

    storeUserSession({
      userData: userData?.userdata || null,
      organizationData: userData?.organization || null,
    })
  }, [userData])

  const inviteStatus = useMemo(() => {
    if (!hasInviteDetails) {
      return {
        status: 'error',
        title: 'Missing invite details',
        description:
          'This invite link is missing required details. Please use the link from your invitation email.',
      }
    }

    if (loading) {
      return {
        status: 'pending',
        title: 'Checking invite',
        description: 'Validating your invite link with the server.',
      }
    }

    if (error) {
      return {
        status: 'error',
        title: 'Unable to verify invite',
        description: 'We could not verify this invite link. Please try again or request a new invite.',
      }
    }

    if (!inviteResponse) {
      return {
        status: 'error',
        title: 'Invite unavailable',
        description: 'We could not load invite details. Please refresh or request a new invite.',
      }
    }

    if (inviteResponse.error) {
      return {
        status: 'error',
        title: 'Invite error',
        description: inviteResponse.error,
      }
    }

    if (inviteResponse.isExpired) {
      return {
        status: 'error',
        title: 'Invite expired',
        description: 'This invite link has expired. Please request a new invite.',
      }
    }

    if (!inviteResponse.hasValidMagicLink) {
      return {
        status: 'error',
        title: 'Invalid invite link',
        description: 'This invite link is not valid. Please request a new invite.',
      }
    }

    if (loadingUserData) {
      return {
        status: 'pending',
        title: 'Signing you in',
        description: 'Loading your account details.',
      }
    }

    if (userDataError) {
      return {
        status: 'error',
        title: 'Signed in, but profile load failed',
        description: 'Your invite was verified, but we could not load your user data. Try again.',
      }
    }

    return {
      status: 'success',
      title: 'Welcome to Metrinome',
      description: "Your invite has been verified. You're all set to start tracking.",
    }
  }, [hasInviteDetails, loading, error, inviteResponse, loadingUserData, userDataError])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg bg-white shadow-sm rounded-2xl p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <img className="w-12 h-12" src={LogoMark} alt="Metrinome" />
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
              inviteStatus.status === 'success'
                ? 'bg-green-100 text-green-700'
                : inviteStatus.status === 'pending'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-rose-100 text-rose-700'
            }`}
          >
            {inviteStatus.status === 'success'
              ? 'Verified'
              : inviteStatus.status === 'pending'
                ? 'Checking'
                : 'Action needed'}
          </span>

          <h1 className="text-2xl font-semibold text-gray-900">{inviteStatus.title}</h1>
          <p className="text-gray-600">{inviteStatus.description}</p>
        </div>

        {inviteStatus.status === 'success' ? (
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-6 text-center">
            <button
              onClick={() => navigate('/trackers')}
              className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-6 py-3 text-sm font-medium text-white hover:bg-indigo-700 transition"
            >
              Go to Trackers
            </button>
          </div>
        ) : (
          <div className="mt-6 text-center text-sm text-gray-500">
            If you need a new link, contact the admin who sent your invite.
          </div>
        )}
      </div>
    </div>
  )
}

export default InvitePage
